import { AxiosError } from 'axios';

/**
 * Backend error response structure
 */
interface BackendErrorResponse {
  message?: string;
  code?: string;
  status?: number;
  details?: {
    fieldErrors?: Record<string, string[]>;
    [key: string]: unknown;
  } | unknown;
  [key: string]: unknown;
}

/**
 * Type guard to check if an error is an Axios error
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object'
  );
}

/**
 * Get the HTTP status code from an error if it's an Axios error
 */
export function getAxiosErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

/**
 * Check if an error is a 404 error
 */
export function is404Error(error: unknown): boolean {
  return getAxiosErrorStatus(error) === 404;
}

/**
 * Get the backend error code from an Axios error response
 */
export function getAxiosErrorCode(error: unknown): string | undefined {
  if (isAxiosError(error)) {
    const data = error.response?.data as BackendErrorResponse | undefined;
    return data?.code;
  }
  return undefined;
}

/**
 * Get the error message from an Axios error response
 */
export function getAxiosErrorMessage(error: unknown): string | undefined {
  if (isAxiosError(error)) {
    const data = error.response?.data as BackendErrorResponse | undefined;
    return data?.message;
  }
  return undefined;
}

function extractFieldErrorSummary(
  details: BackendErrorResponse['details']
): string | undefined {
  if (!details || typeof details !== 'object') {
    return undefined;
  }

  const maybeFieldErrors = (details as { fieldErrors?: unknown }).fieldErrors;
  if (!maybeFieldErrors || typeof maybeFieldErrors !== 'object') {
    return undefined;
  }

  const summaries: string[] = [];
  for (const [field, messages] of Object.entries(
    maybeFieldErrors as Record<string, unknown>
  )) {
    if (!Array.isArray(messages)) {
      continue;
    }
    for (const message of messages) {
      if (typeof message === 'string' && message.trim().length > 0) {
        summaries.push(`${field}: ${message}`);
      }
    }
  }

  if (summaries.length === 0) {
    return undefined;
  }
  return summaries.join(' | ');
}

export function getAxiosErrorDisplayMessage(
  error: unknown,
  fallbackMessage: string
): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as BackendErrorResponse | undefined;
    const message = data?.message?.trim();
    const fieldErrorSummary = extractFieldErrorSummary(data?.details);

    if (message && fieldErrorSummary) {
      return `${message} (${fieldErrorSummary})`;
    }
    if (message) {
      return message;
    }
    if (fieldErrorSummary) {
      return fieldErrorSummary;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallbackMessage;
}

